import React, { Component, createContext } from "react";
import { firestore } from "../firebase";
import Helper from "../components/helper";

export const PLANITEM_RETURN_CONTEXT = createContext();
export const PLANITEM_RETURN_PAGING_CONTEXT = createContext();


// Build one Invoice row per delivery order (matching the original "query the
// delivery order, show the DO info" behavior). A delivery order reaches
// state === "r" only once ALL of its containers are returned, so each order is
// surfaced exactly once — no per-container duplication of the DO#. The row label
// lists every container's invoice line, built from the order via getInvoiceShortDetail.
export function buildInvoiceRowsFromDeliveryOrders(orders) {
  return (orders || []).reduce((rows, order) => {
    if (!order || order.invoiced === true) {
      return rows;
    }

    const refs = Array.isArray(order.c_Ref) ? order.c_Ref : [];
    const nums = Array.isArray(order.c_Num) ? order.c_Num : [];

    // One DO-level line per row (matching the original invoice screen) — not a
    // per-container breakdown. Built from the delivery order's first container.
    const shortDetail = Helper.getInvoiceShortDetail(order, { ref: refs[0], number: nums[0] }, 0);

    rows.push({
      ...order,
      d_Ref: order.id,
      cIndex: 0,
      routingContainerState: "r",
      shortDetail: shortDetail,
    });

    return rows;
  }, []);
}

class PlanItemRETURNProvider extends Component {
  state = {
    return_planItems: undefined,
    isLoading: false,
    error: null,
  };

  lastRequestToken = 0;

  getBaseQuery = () => {
    const deliveryOrdersCollection = Helper.getBranchCollectionName("deliverOrders", this.props.branchCode);
    // Delivery-order source: state === "r" = fully returned. Filter out invoiced orders
    // SERVER-SIDE (invoiced != true → the "" / false / not-yet-invoiced set) so we only
    // fetch the active invoice list, not the whole returned+invoiced history. This is the
    // fix for the slow (~10s) load. Requires a (state, invoiced) composite index — see
    // firestore.indexes.json.
    return firestore
      .collection(deliveryOrdersCollection)
      .where("state", "==", "r")
      .where("invoiced", "!=", true);
  };

  loadPage = async () => {
    const requestToken = ++this.lastRequestToken;

    // Fetches only the not-invoiced returned orders (state == "r" AND invoiced != true)
    // server-side — the whole active invoice list in one query, one row per DO.
    // NOTE: We intentionally do NOT clear return_planItems to undefined here during
    // a refresh — doing so would unmount the VariableSizeGrid and reset scroll position.
    // Only the initial load (via resetAndLoad) sets it to undefined to show the spinner.
    this.setState({ isLoading: true, error: null });

    try {
      const snapshot = await this.getBaseQuery().get();
      if (requestToken !== this.lastRequestToken) {
        return;
      }

      const orderObjs = snapshot.docs.map(doc => {
        return { id: doc.id, ...doc.data() };
      });

      const notInvoicedList = buildInvoiceRowsFromDeliveryOrders(orderObjs);
      if (requestToken !== this.lastRequestToken) {
        return;
      }

      this.setState({
        return_planItems: notInvoicedList,
        isLoading: false,
      });
    } catch (err) {
      if (requestToken !== this.lastRequestToken) {
        return;
      }

      console.error(`Error getting return plan items (${this.props.branchCode || "NY"})`, err);
      this.setState({
        return_planItems: this.state.return_planItems || [],
        isLoading: false,
        error: err,
      });
    }
  };

  refreshPage = () => {
    this.loadPage();
  };

  resetAndLoad = () => {
    this.lastRequestToken += 1;
    this.setState({
      return_planItems: undefined,
      isLoading: false,
      error: null,
    }, () => this.loadPage());
  };

  componentDidMount = () => {
    this.loadPage();
  };

  componentDidUpdate = prevProps => {
    if ((prevProps.branchCode || "NY") !== (this.props.branchCode || "NY")) {
      this.resetAndLoad();
    }
  };

  componentWillUnmount = () => {
    this.lastRequestToken += 1;
  };

  render() {
    const { return_planItems, isLoading, error } = this.state;
    const { children } = this.props;
    const pagingValue = {
      error,
      isLoading,
      refreshPage: this.refreshPage,
    };

    return (
      <PLANITEM_RETURN_CONTEXT.Provider value={return_planItems}>
        <PLANITEM_RETURN_PAGING_CONTEXT.Provider value={pagingValue}>
          {children}
        </PLANITEM_RETURN_PAGING_CONTEXT.Provider>
      </PLANITEM_RETURN_CONTEXT.Provider>
    );
  }
}

export default PlanItemRETURNProvider;

PlanItemRETURNProvider.defaultProps = {
  branchCode: "NY",
};
